import { useEffect, useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Link2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { api, type SavedLink, type SavedLinkColumn } from "@/lib/api";
import { cn } from "@/lib/utils";

const COLUMN_ORDER: SavedLinkColumn[] = ["arek", "wspolne", "kasia"];
const COLUMN_SET = new Set<string>(COLUMN_ORDER);

const COLUMN_META: Record<
  SavedLinkColumn,
  { title: string; rail: string; header: string; empty: string }
> = {
  arek: {
    title: "Arek",
    rail: "border-l-blue-500",
    header: "text-blue-700",
    empty: "Brak linków Arka",
  },
  wspolne: {
    title: "Wspólne",
    rail: "border-l-amber-500",
    header: "text-amber-800",
    empty: "Brak wspólnych linków",
  },
  kasia: {
    title: "Kasia",
    rail: "border-l-pink-500",
    header: "text-pink-700",
    empty: "Brak linków Kasi",
  },
};

const boardCollision: CollisionDetection = (args) => {
  const preferCardOrColumn = (hits: ReturnType<typeof pointerWithin>) => {
    const cardHit = hits.find((c) => !COLUMN_SET.has(String(c.id)));
    if (cardHit) return [cardHit];
    const colHit = hits.find((c) => COLUMN_SET.has(String(c.id)));
    if (colHit) return [colHit];
    return hits.slice(0, 1);
  };
  const pointerHits = pointerWithin(args);
  if (pointerHits.length) return preferCardOrColumn(pointerHits);
  const rectHits = rectIntersection(args);
  if (rectHits.length) return preferCardOrColumn(rectHits);
  return closestCorners(args);
};

function useIsMobileMd(): boolean {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

function isColumnId(id: string): id is SavedLinkColumn {
  return COLUMN_SET.has(id);
}

function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function nextColumn(col: SavedLinkColumn): SavedLinkColumn | null {
  const i = COLUMN_ORDER.indexOf(col);
  return i >= 0 && i < COLUMN_ORDER.length - 1 ? COLUMN_ORDER[i + 1] : null;
}

function prevColumn(col: SavedLinkColumn): SavedLinkColumn | null {
  const i = COLUMN_ORDER.indexOf(col);
  return i > 0 ? COLUMN_ORDER[i - 1] : null;
}

function LinkCard({
  link,
  isDragging,
  onEditTitle,
  onDelete,
  onMoveLeft,
  onMoveRight,
  showMoveButtons,
}: {
  link: SavedLink;
  isDragging?: boolean;
  onEditTitle: (link: SavedLink) => void;
  onDelete: (link: SavedLink) => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
  showMoveButtons?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-slate-200 bg-white p-3 shadow-sm",
        isDragging && "opacity-80 shadow-md ring-2 ring-brand/30",
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900">{link.title}</p>
          <a
            href={link.url}
            target="_blank"
            rel="noreferrer"
            className="mt-0.5 inline-flex max-w-full items-center gap-1 truncate text-xs text-slate-500 hover:text-brand"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3 w-3 shrink-0" />
            <span className="truncate">{hostOf(link.url)}</span>
          </a>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            title="Edytuj tytuł"
            onClick={(e) => {
              e.stopPropagation();
              onEditTitle(link);
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-rose-600 hover:text-rose-700"
            title="Usuń"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(link);
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {showMoveButtons ? (
        <div className="mt-2 flex gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 flex-1 px-2 text-xs"
            disabled={!onMoveLeft}
            onClick={(e) => {
              e.stopPropagation();
              onMoveLeft?.();
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <ArrowLeft className="mr-1 h-3 w-3" />
            W lewo
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 flex-1 px-2 text-xs"
            disabled={!onMoveRight}
            onClick={(e) => {
              e.stopPropagation();
              onMoveRight?.();
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            W prawo
            <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function SortableLinkCard({
  link,
  onEditTitle,
  onDelete,
  onMoveLeft,
  onMoveRight,
  showMoveButtons,
}: {
  link: SavedLink;
  onEditTitle: (link: SavedLink) => void;
  onDelete: (link: SavedLink) => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
  showMoveButtons?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: link.id,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="touch-none">
      <LinkCard
        link={link}
        isDragging={isDragging}
        onEditTitle={onEditTitle}
        onDelete={onDelete}
        onMoveLeft={onMoveLeft}
        onMoveRight={onMoveRight}
        showMoveButtons={showMoveButtons}
      />
    </div>
  );
}

function ColumnDroppable({
  columnId,
  children,
}: {
  columnId: SavedLinkColumn;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: columnId });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex min-h-[12rem] flex-1 flex-col gap-2 rounded-xl bg-slate-50/80 p-2",
        isOver && "bg-brand/5 ring-1 ring-brand/20",
      )}
    >
      {children}
    </div>
  );
}

export default function Linki() {
  const qc = useQueryClient();
  const isMobile = useIsMobileMd();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [mobileCol, setMobileCol] = useState<SavedLinkColumn>("wspolne");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [newColumn, setNewColumn] = useState<SavedLinkColumn>("wspolne");

  const { data, isLoading, error } = useQuery({
    queryKey: ["saved-links"],
    queryFn: () => api.savedLinksList(),
  });

  const items = data?.items || [];
  const byColumn = useMemo(() => {
    const m: Record<SavedLinkColumn, SavedLink[]> = {
      arek: [],
      wspolne: [],
      kasia: [],
    };
    for (const col of data?.columns || []) {
      if (isColumnId(col.id)) m[col.id] = [...col.items].sort((a, b) => a.sort_order - b.sort_order);
    }
    return m;
  }, [data]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const invalidate = () => qc.invalidateQueries({ queryKey: ["saved-links"] });

  const mCreate = useMutation({
    mutationFn: () =>
      api.savedLinksCreate({
        title: title.trim(),
        url: url.trim(),
        column_id: isMobile ? mobileCol : newColumn,
      }),
    onSuccess: () => {
      setTitle("");
      setUrl("");
      invalidate();
    },
  });

  const mUpdate = useMutation({
    mutationFn: ({ id, title: nextTitle }: { id: string; title: string }) =>
      api.savedLinksUpdate(id, { title: nextTitle }),
    onSuccess: () => invalidate(),
  });

  const mDelete = useMutation({
    mutationFn: (id: string) => api.savedLinksDelete(id),
    onSuccess: () => invalidate(),
  });

  const mMove = useMutation({
    mutationFn: ({ id, column_id }: { id: string; column_id: SavedLinkColumn }) =>
      api.savedLinksMove(id, column_id),
    onSuccess: () => invalidate(),
  });

  const mReorder = useMutation({
    mutationFn: ({
      column_id,
      orderedIds,
    }: {
      column_id: SavedLinkColumn;
      orderedIds: string[];
    }) => api.savedLinksReorder(column_id, orderedIds),
    onSuccess: () => invalidate(),
  });

  const onEditTitle = (link: SavedLink) => {
    const next = window.prompt("Tytuł linku", link.title);
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === link.title) return;
    mUpdate.mutate({ id: link.id, title: trimmed });
  };

  const onDelete = (link: SavedLink) => {
    if (!window.confirm(`Usunąć „${link.title}"?`)) return;
    mDelete.mutate(link.id);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !url.trim()) return;
    mCreate.mutate();
  };

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));

  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const linkId = String(active.id);
    const overId = String(over.id);
    if (linkId === overId) return;

    const activeLink = items.find((t) => t.id === linkId);
    if (!activeLink) return;

    const overColumn: SavedLinkColumn | undefined = isColumnId(overId)
      ? overId
      : items.find((t) => t.id === overId)?.column_id;
    if (!overColumn) return;

    const sourceList = byColumn[activeLink.column_id];
    const destList = byColumn[overColumn];
    const oldIndex = sourceList.findIndex((t) => t.id === linkId);
    if (oldIndex < 0) return;

    if (activeLink.column_id === overColumn) {
      const newIndex = isColumnId(overId)
        ? destList.length - 1
        : destList.findIndex((t) => t.id === overId);
      if (newIndex < 0 || oldIndex === newIndex) return;
      mReorder.mutate({
        column_id: overColumn,
        orderedIds: arrayMove(sourceList, oldIndex, newIndex).map((t) => t.id),
      });
      return;
    }

    if (destList.length === 0) {
      mMove.mutate({ id: linkId, column_id: overColumn });
      return;
    }

    if (isColumnId(overId)) {
      mReorder.mutate({
        column_id: overColumn,
        orderedIds: [...destList.map((t) => t.id), linkId],
      });
      return;
    }

    const overIndex = destList.findIndex((t) => t.id === overId);
    const insertAt = overIndex < 0 ? destList.length : overIndex;
    const nextDest = destList.filter((t) => t.id !== linkId);
    nextDest.splice(insertAt, 0, activeLink);
    mReorder.mutate({
      column_id: overColumn,
      orderedIds: nextDest.map((t) => t.id),
    });
  };

  const activeLink = items.find((t) => t.id === activeId) || null;

  const createForm = (
    <form
      onSubmit={onSubmit}
      className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center"
    >
      {!isMobile ? (
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
          {COLUMN_ORDER.map((col) => (
            <Button
              key={col}
              type="button"
              size="sm"
              variant={newColumn === col ? "default" : "ghost"}
              className="h-8 px-3"
              onClick={() => setNewColumn(col)}
            >
              {COLUMN_META[col].title}
            </Button>
          ))}
        </div>
      ) : null}
      <Input
        placeholder="Tytuł"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="min-w-0 flex-1 bg-white sm:max-w-[12rem]"
      />
      <Input
        placeholder="https://…"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        className="min-w-0 flex-1 bg-white"
      />
      <Button
        type="submit"
        size="sm"
        disabled={!title.trim() || !url.trim() || mCreate.isPending}
        className="shrink-0"
      >
        <Plus className="mr-1 h-3.5 w-3.5" /> Dodaj
      </Button>
    </form>
  );

  const renderColumn = (columnId: SavedLinkColumn) => {
    const meta = COLUMN_META[columnId];
    const list = byColumn[columnId];
    return (
      <Card key={columnId} className={cn("flex min-h-0 flex-col border-l-4", meta.rail)}>
        <CardHeader className="pb-2">
          <CardTitle className={cn("flex items-center justify-between text-base", meta.header)}>
            <span>{meta.title}</span>
            <span className="text-xs font-normal text-muted-foreground">{list.length}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col pt-0">
          <ColumnDroppable columnId={columnId}>
            <SortableContext items={list.map((l) => l.id)} strategy={verticalListSortingStrategy}>
              {list.length === 0 ? (
                <p className="px-1 py-6 text-center text-xs text-muted-foreground">{meta.empty}</p>
              ) : (
                list.map((link) => (
                  <SortableLinkCard
                    key={link.id}
                    link={link}
                    onEditTitle={onEditTitle}
                    onDelete={onDelete}
                    showMoveButtons={isMobile}
                    onMoveLeft={
                      prevColumn(link.column_id)
                        ? () =>
                            mMove.mutate({
                              id: link.id,
                              column_id: prevColumn(link.column_id)!,
                            })
                        : undefined
                    }
                    onMoveRight={
                      nextColumn(link.column_id)
                        ? () =>
                            mMove.mutate({
                              id: link.id,
                              column_id: nextColumn(link.column_id)!,
                            })
                        : undefined
                    }
                  />
                ))
              )}
            </SortableContext>
          </ColumnDroppable>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="mx-0 max-w-full overflow-x-hidden rounded-2xl border border-border bg-[#f7f8fa] p-3 text-foreground shadow-sm sm:p-6">
      <PageHeader
        title="Linki"
        subtitle="Bookmarki w trzech kolumnach: Arek, Wspólne, Kasia"
        actions={!isMobile ? createForm : undefined}
      />

      {isMobile ? (
        <div className="mb-3 space-y-3">
          {createForm}
          <div className="grid grid-cols-3 gap-1 rounded-xl border border-slate-200 bg-white p-1">
            {COLUMN_ORDER.map((col) => (
              <Button
                key={col}
                type="button"
                size="sm"
                variant={mobileCol === col ? "default" : "ghost"}
                className="h-8"
                onClick={() => setMobileCol(col)}
              >
                {COLUMN_META[col].title}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Ładowanie…</p>
      ) : error ? (
        <p className="text-sm text-rose-600">Nie udało się wczytać linków.</p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={boardCollision}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          {isMobile ? (
            <div className="grid grid-cols-1 gap-3">{renderColumn(mobileCol)}</div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">{COLUMN_ORDER.map(renderColumn)}</div>
          )}
          <DragOverlay>
            {activeLink ? (
              <div className="w-[min(20rem,90vw)]">
                <LinkCard
                  link={activeLink}
                  isDragging
                  onEditTitle={() => undefined}
                  onDelete={() => undefined}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {!isLoading && !error && items.length === 0 ? (
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Link2 className="h-4 w-4" />
          Dodaj pierwszy link, żeby nic nie zginęło.
        </div>
      ) : null}

      {mCreate.isError ? (
        <p className="mt-3 text-sm text-rose-600">
          {(mCreate.error as Error)?.message || "Nie udało się dodać linku."}
        </p>
      ) : null}
    </div>
  );
}
